import React, { useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Typography,
} from "@mui/material";
import { Close, FileDownload, UploadFile } from "@mui/icons-material";
import { colors, typography } from "../../theme/designSystem";

/**
 * Importação de planilha em dois tempos (pedido do Hilton, 19/09/2026): escolher o arquivo mostra
 * a **prévia** — quantas linhas entram, quantas atualizam, quantas ficam de fora e por quê — e só
 * depois se confirma. É o passo que evita descobrir o estrago com trinta registros já criados.
 *
 * <p>Genérico de propósito: a mesma mecânica serve aos procedimentos do convênio (Tipos de
 * serviço) e aos valores (Valores por serviço). Quem usa passa as duas funções e os rótulos.</p>
 */
export interface ImportOutcome {
  created: number;
  updated: number;
  /** Só a importação de valores tem "sem mudança"; nas demais vem indefinido. */
  unchanged?: number;
  skipped: number;
  issues: { row: number; reason: string; procedure?: string; professional?: string }[];
}

interface Props {
  open: boolean;
  title: string;
  /** Uma linha dizendo o que a planilha faz — some assim que a prévia aparece. */
  hint: string;
  /** Quando existe, o modal oferece baixar o modelo antes do upload. */
  onDownloadTemplate?: () => Promise<void> | void;
  templateLabel?: string;
  /** dryRun = true na prévia; false ao confirmar. */
  onSend: (file: File, dryRun: boolean) => Promise<ImportOutcome>;
  onClose: () => void;
  onDone: (outcome: ImportOutcome) => void;
}

const ImportSheetModal: React.FC<Props> = ({
  open, title, hint, onDownloadTemplate, templateLabel = "Baixar modelo", onSend, onClose, onDone,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");

  const reset = () => {
    setFile(null);
    setPreview(null);
    setBusy(false);
    setErro("");
  };

  const fechar = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const escolher = async (chosen: File | null) => {
    setFile(chosen);
    setPreview(null);
    setErro("");
    if (!chosen) return;
    setBusy(true);
    try {
      setPreview(await onSend(chosen, true));
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { description?: string } } })?.response?.data?.description;
      setErro(message || "Não foi possível ler a planilha.");
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmar = async () => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const outcome = await onSend(file, false);
      onDone(outcome);
      reset();
      onClose();
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { description?: string } } })?.response?.data?.description;
      setErro(message || "Não foi possível importar a planilha.");
      setBusy(false);
    }
  };

  const nadaAFazer = preview !== null && preview.created === 0 && preview.updated === 0;

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{
        backgroundColor: colors.primary, color: colors.white, display: "flex",
        justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem",
      }}>
        <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
          {title}
        </Typography>
        <IconButton onClick={fechar} disabled={busy} sx={{ color: colors.white, padding: "0.25rem" }} aria-label="Fechar">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
        {!preview && (
          <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 2 }}>{hint}</Typography>
        )}

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: preview ? 2 : 0 }}>
          {onDownloadTemplate && (
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => onDownloadTemplate()}
              disabled={busy}
              sx={{ textTransform: "none" }}
            >
              {templateLabel}
            </Button>
          )}
          <Button
            component="label"
            variant="contained"
            startIcon={busy && !preview ? <CircularProgress size={16} sx={{ color: "white" }} /> : <UploadFile />}
            disabled={busy}
            sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {file ? "Trocar arquivo" : "Escolher planilha"}
            <input
              type="file"
              hidden
              accept=".xlsx,.csv"
              onChange={(e) => escolher(e.target.files?.[0] ?? null)}
            />
          </Button>
          {file && (
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{file.name}</Typography>
          )}
        </Box>

        {erro && <Typography sx={{ fontSize: 13, color: "#dc3545", mt: 1 }}>{erro}</Typography>}

        {preview && (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>O que vai acontecer</Typography>
            <Typography sx={{ fontSize: 13, color: colors.text }}>
              {[`${preview.created} novo(s)`,
                `${preview.updated} atualizado(s)`,
                preview.unchanged !== undefined ? `${preview.unchanged} sem mudança` : null,
                `${preview.skipped} fora`,
              ].filter(Boolean).join(" · ")}
            </Typography>

            {preview.issues.length > 0 && (
              <Box sx={{ mt: 1.5, maxHeight: 220, overflowY: "auto", border: `1px solid ${colors.backgroundAlt}`, borderRadius: "8px" }}>
                {preview.issues.map((i) => (
                  <Box key={`${i.row}-${i.reason}`} sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${colors.backgroundAlt}` }}>
                    <Typography sx={{ fontSize: 12.5 }}>
                      <strong>Linha {i.row}</strong>
                      {i.procedure || i.professional ? ` · ${i.procedure || i.professional}` : ""} — {i.reason}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {nadaAFazer && (
              <Typography sx={{ fontSize: 13, color: "#8a6100", mt: 1.5 }}>
                Nenhuma linha entraria. Corrija a planilha e escolha de novo.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        padding: "1.5rem 2rem",
        borderTop: `1px solid ${colors.backgroundAlt}`,
        backgroundColor: colors.background,
        gap: "1rem",
      }}>
        <Button onClick={fechar} variant="outlined" disabled={busy} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          onClick={confirmar}
          variant="contained"
          disabled={busy || !preview || nadaAFazer}
          sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
        >
          {busy && preview ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportSheetModal;
